<script setup lang="ts">
import {onMounted, watch, ref} from 'vue';
import {storeToRefs} from 'pinia';
import {useCrmAccountStore, useCrmMetadataStore, useValueDataStore} from '@/stores';
import {salesOrdersFields} from '@/crm/spec/salesOrdersFields';
import type {EntityName, FieldDef, SectionDef} from '@/crm/spec/types';
import {readField} from '@/crm/odata';
import {DownIcon} from '@/components/icons';
import Spinner from '@/components/common/Spinner.vue';

const valueStore = useValueDataStore();
const crmStore = useCrmAccountStore();
const metadataStore = useCrmMetadataStore();

const {selectionId} = storeToRefs(valueStore);
const {
    account, salesOrders, orderDetails, orderDetailsLoading,
    isAccountLoading, isSalesOrdersLoading, accountError,
} = storeToRefs(crmStore);
const {entities} = storeToRefs(metadataStore);

const ordersSection: SectionDef = salesOrdersFields.sections.find(s => s.entity === 'salesorder')!;
const detailsSection: SectionDef = salesOrdersFields.sections.find(s => s.entity === 'salesorderdetail')!;
const orderColumns: FieldDef[] = ordersSection.columns ?? [];
const detailColumns: FieldDef[] = detailsSection.columns ?? [];
const ordersEntity: EntityName = ordersSection.entity;
const detailsEntity: EntityName = detailsSection.entity;

const expanded = ref<Record<string, boolean>>({});

async function load() {
    await crmStore.ensureSalesOrders();
    metadataStore.ensureEntity(ordersEntity);
    metadataStore.ensureEntity(detailsEntity);
}

function toggle(orderId: string) {
    const next = !expanded.value[orderId];
    expanded.value = {...expanded.value, [orderId]: next};
    if (next) crmStore.ensureOrderDetails(orderId);
}

function header(entity: EntityName, def: FieldDef): string {
    if (def.labelOverride) return def.labelOverride;
    void entities.value;
    return metadataStore.getLabel(entity, def.field);
}

function cell(record: any, def: FieldDef): string {
    return readField(record, def.field) ?? '-';
}

onMounted(load);
watch(selectionId, load);
</script>

<template>
    <div class="crm-page">
        <div v-if="!selectionId" class="placeholder">Select a store or group to view its CRM record.</div>

        <template v-else>
            <div v-if="isAccountLoading && !account" class="loading"><Spinner/></div>
            <div v-else-if="accountError" class="error">Couldn't load account.</div>

            <template v-else-if="account">
                <section class="orders">
                    <h2>{{ ordersSection.name }}</h2>

                    <div v-if="isSalesOrdersLoading && !salesOrders.length" class="loading-inline"><Spinner/></div>
                    <div v-else-if="!salesOrders.length" class="empty">No sales orders</div>

                    <table v-else class="orders-table">
                        <thead>
                            <tr>
                                <th class="chevron-col"></th>
                                <th v-for="(c, i) in orderColumns" :key="i">{{ header(ordersEntity, c) }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="order in salesOrders" :key="order.salesorderid">
                                <tr class="order-row" :class="{expanded: expanded[order.salesorderid]}">
                                    <td class="chevron-col">
                                        <button
                                            type="button"
                                            class="chevron"
                                            :class="{open: expanded[order.salesorderid]}"
                                            :aria-expanded="!!expanded[order.salesorderid]"
                                            @click="toggle(order.salesorderid)"
                                        >
                                            <DownIcon/>
                                        </button>
                                    </td>
                                    <td
                                        v-for="(c, ci) in orderColumns"
                                        :key="ci"
                                        :class="{empty: cell(order, c) === '-'}"
                                    >
                                        {{ cell(order, c) }}
                                    </td>
                                </tr>
                                <tr v-if="expanded[order.salesorderid]" class="detail-row">
                                    <td :colspan="orderColumns.length + 1" class="detail-cell">
                                        <div v-if="orderDetailsLoading[order.salesorderid]" class="loading-inline"><Spinner/></div>
                                        <div v-else-if="!(orderDetails[order.salesorderid]?.length)" class="empty">
                                            No order items
                                        </div>
                                        <table v-else class="detail-table">
                                            <thead>
                                                <tr>
                                                    <th v-for="(c, di) in detailColumns" :key="di">{{ header(detailsEntity, c) }}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="item in (orderDetails[order.salesorderid] ?? [])" :key="item.salesorderdetailid">
                                                    <td
                                                        v-for="(c, ci) in detailColumns"
                                                        :key="ci"
                                                        :class="{empty: cell(item, c) === '-'}"
                                                    >
                                                        {{ cell(item, c) }}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </section>
            </template>
        </template>
    </div>
</template>

<style scoped>
.crm-page {
    padding: 1.5rem;
    max-width: 1400px;
    margin: 0 auto;
    color: var(--color-text);
}

.orders {
    background: var(--color-background-bright);
    border: 1px solid var(--color-border, rgba(0, 0, 0, 0.08));
    border-radius: 0.5rem;
    padding: 1rem 1.25rem 1.25rem;
}

.orders h2 {
    margin: 0 0 0.75rem;
    font-size: var(--font-size-h2, 1.1rem);
    font-weight: 600;
    color: var(--color-text);
}

.orders-table,
.detail-table {
    width: 100%;
    border-collapse: collapse;
}

.orders-table th,
.orders-table td,
.detail-table th,
.detail-table td {
    padding: 0.4rem 0.75rem 0.4rem 0;
    font-size: var(--font-size-body);
    color: var(--color-text);
    border-bottom: 1px solid var(--color-border, rgba(0, 0, 0, 0.06));
    vertical-align: top;
    text-align: left;
}

.orders-table th,
.detail-table th {
    font-weight: 600;
    opacity: 0.75;
}

.orders-table td.empty,
.detail-table td.empty {
    opacity: 0.4;
}

.chevron-col {
    width: 2rem;
    padding-right: 0;
}

.chevron {
    background: none;
    border: none;
    padding: 0.25rem;
    color: var(--color-text);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.15s ease;
}

.chevron.open {
    transform: rotate(180deg);
}

.detail-row .detail-cell {
    padding: 0.5rem 0.5rem 1rem 2rem;
    background: var(--color-background, rgba(0, 0, 0, 0.02));
    border-bottom: 1px solid var(--color-border, rgba(0, 0, 0, 0.06));
}

.detail-table {
    background: transparent;
}

.placeholder, .error {
    padding: 2rem;
    text-align: center;
    opacity: 0.7;
    font-size: var(--font-size-body);
}

.empty {
    opacity: 0.6;
    font-size: var(--font-size-body);
    padding: 0.5rem 0;
}

.loading {
    display: flex;
    justify-content: center;
    padding: 3rem;
}

.loading-inline {
    display: flex;
    justify-content: center;
    padding: 0.5rem;
}
</style>
